/**
 * customButtonActions — dispatch akcji własnych przycisków nawigacyjnych
 * (Kreator Listy → zakładka „Przyciski"). Tylko akcje frontowe:
 *   navigate  → przejście routerem na ścieżkę,
 *   open_url  → otwarcie adresu (ta sama / nowa karta),
 *   refresh   → odświeżenie danych listy (callback callera).
 *
 * Akcje wykonujące coś po stronie serwera (mail / webhook / zadanie / zmiana
 * pola) = automatyzacja — dojdą w późniejszym etapie razem z silnikiem akcji.
 *
 * `resolveIcon` mapuje opcjonalną nazwę ikony lucide (np. „Plus") na komponent.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { CustomButtonAction } from '@saas/shared';
import type { LucideIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

export interface CustomActionContext {
  navigate: NavigateFunction;
  refresh: () => void;
}

/** Rekordy, na których klik działa — akcja wiersza (`{id}`) albo masowa (`{ids}`). */
export interface CustomActionTarget {
  ids: readonly string[];
}

/**
 * Podstawia zmienne rekordu w ścieżce/adresie: `{id}` → id (pierwszego) rekordu,
 * `{ids}` → id zaznaczonych po przecinku. Bez celu (przycisk paska) zmienne zostają.
 */
export function interpolateCustomTarget(template: string, target?: CustomActionTarget): string {
  if (!target || target.ids.length === 0) return template;
  return template
    .replace(/\{id\}/g, encodeURIComponent(target.ids[0] ?? ''))
    .replace(/\{ids\}/g, target.ids.map(encodeURIComponent).join(','));
}

/** Schematy adresów, które przycisk może otworzyć. Inne (`javascript:`, `data:`…) — nigdy. */
const SAFE_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** Zmienna `{id}`/`{ids}`, której nie było czym podstawić (np. przycisk paska). */
const UNRESOLVED_VAR = /\{ids?\}/;

/**
 * Adres do otwarcia albo `null`, gdy nie wolno go otworzyć. Bez schematu (np. `www.firma.pl`)
 * → `https://`; ścieżka zaczynająca się od `/` — wewnątrz aplikacji. Schemat spoza listy
 * bezpiecznych (`javascript:` wykonałby skrypt w panelu klikającej osoby) → `null`.
 * Używane też przy zapisie przycisku w Kreatorze Listy.
 */
export function safeExternalUrl(raw: string): string | null {
  const url = raw.trim();
  if (!url) return null;
  // Ścieżka w aplikacji — ale nie `//host` ani `/\host` (przeglądarka czyta `\` jak `/`,
  // więc oba prowadzą na obcą stronę).
  if (url.startsWith('/')) return /^\/[/\\]/.test(url) ? null : url;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1];
  if (!scheme) return `https://${url}`;
  return SAFE_URL_SCHEMES.has(`${scheme.toLowerCase()}:`) ? url : null;
}

export function runCustomAction(
  action: CustomButtonAction,
  ctx: CustomActionContext,
  target?: CustomActionTarget,
): void {
  switch (action.type) {
    case 'navigate': {
      const to = interpolateCustomTarget(action.to.trim(), target);
      if (!to || UNRESOLVED_VAR.test(to)) return;
      ctx.navigate(to.startsWith('/') ? to : `/${to}`);
      break;
    }
    case 'open_url': {
      const interpolated = interpolateCustomTarget(action.url, target);
      const url = UNRESOLVED_VAR.test(interpolated) ? null : safeExternalUrl(interpolated);
      if (!url) return;
      if (action.newTab) window.open(url, '_blank', 'noopener,noreferrer');
      else window.location.assign(url);
      break;
    }
    case 'refresh':
      ctx.refresh();
      break;
  }
}

const ICON_REGISTRY = LucideIcons as unknown as Record<string, LucideIcon>;

/** Zwraca komponent ikony lucide po nazwie (PascalCase) lub null gdy brak. */
export function resolveIcon(name: string | undefined): LucideIcon | null {
  if (!name) return null;
  return ICON_REGISTRY[name] ?? null;
}
